import { useState, useEffect, useCallback } from "react";
import api from "../api";
import FacturaVista from "./FacturaVista";
import { useConfirm } from "./ConfirmDialog";

async function safe(fn) { try { return await fn(); } catch { return null; } }

function Alert({ msg, onClose }) {
  if (!msg) return null;
  const cls = {success:"nv-alert-ok",error:"nv-alert-err",warn:"nv-alert-warn"}[msg.type]||"nv-alert-ok";
  return (
    <div className={"nv-alert "+cls} style={{ marginBottom:14 }}>
      <span style={{ flex:1 }}>{msg.text}</span>
      <button onClick={onClose} style={{ background:"none",border:"none",cursor:"pointer",color:"inherit",fontSize:14 }}>✕</button>
    </div>
  );
}

function fmt$(v) { return v!=null?"$"+parseFloat(v).toFixed(2):"—"; }

const STATUS_BADGE = {
  paid:      "nv-badge-ok",
  sent:      "nv-badge-info",
  overdue:   "nv-badge-err",
  draft:     "nv-badge-muted",
  cancelled: "nv-badge-muted",
};

export default function Billing() {
  const [stats,   setStats]   = useState(null);
  const [invoices,setInvoices]= useState([]);
  const [config,  setConfig]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab,     setTab]     = useState("dashboard");
  const [msg,     setMsg]     = useState(null);
  const [busy,    setBusy]    = useState(false);
  const [filters, setFilters] = useState({ status:"", period:"" });
  const [facturaId, setFacturaId] = useState(null);
  const [emisor,   setEmisor]   = useState(null);
  const [categorias,setCategorias]= useState([]);
  // window.confirm no abre cartel en el navegador y devuelve false: cancelaba solo.
  const [confirmar, dialogoConfirm] = useConfirm();

  const showMsg = (text,type="success") => { setMsg({text,type}); setTimeout(()=>setMsg(null),5000); };

  const load = useCallback(async () => {
    setLoading(true);
    const now = new Date();
    const period = now.getFullYear()+"-"+String(now.getMonth()+1).padStart(2,"0");
    const [s,inv,cfg,em,cat] = await Promise.all([
      safe(()=>api.get("/billing/stats")),
      safe(()=>api.get("/billing/invoices?period="+period)),
      safe(()=>api.get("/billing/config")),
      safe(()=>api.get("/billing/emisor")),
      safe(()=>api.get("/billing/categorias-codigo")),
    ]);
    setStats(s?.data||null);
    setInvoices(inv?.data?.data||[]);
    setConfig(cfg?.data||null);
    setEmisor(em?.data&&Object.keys(em.data).length?em.data:null);
    setCategorias(cat?.data?.data||[]);
    setLoading(false);
  },[]);

  useEffect(()=>{ load(); },[load]);

  const handleGenerate = async () => {
    setBusy(true);
    try {
      const now = new Date();
      const period = now.getFullYear()+"-"+String(now.getMonth()+1).padStart(2,"0");
      const r = await api.post("/billing/generate",{ period });
      showMsg("Generadas "+r.data.generated+" facturas para "+r.data.period);
      load();
    } catch(e) { showMsg(e?.response?.data?.detail||"Error al generar","error"); }
    finally { setBusy(false); }
  };

  const handleProcessOverdue = async () => {
    if (!(await confirmar("Procesar facturas vencidas y suspender clientes con impago?"))) return;
    setBusy(true);
    try {
      const r = await api.post("/billing/process-overdue");
      showMsg("Vencidas: "+r.data.overdue_invoices+" · Suspendidos: "+r.data.suspended_clients);
      load();
    } catch(e) { showMsg(e?.response?.data?.detail||"Error","error"); }
    finally { setBusy(false); }
  };

  const handleMarkPaid = async (id) => {
    try {
      await api.post("/billing/invoices/"+id+"/pay");
      showMsg("Factura marcada como pagada");
      load();
    } catch(e) { showMsg("Error","error"); }
  };

  const handleSaveConfig = async () => {
    setBusy(true);
    try {
      await api.put("/billing/config", config);
      showMsg("Configuración guardada");
    } catch(e) { showMsg(e?.response?.data?.detail||"Error","error"); }
    finally { setBusy(false); }
  };

  const handleSaveEmisor = async () => {
    setBusy(true);
    try {
      await api.put("/billing/emisor", emisor);
      showMsg("Datos del emisor guardados");
    } catch(e) { showMsg(e?.response?.data?.detail||"Error","error"); }
    finally { setBusy(false); }
  };

  const setEm = (k,v) => setEmisor(e=>({...e,[k]:v}));

  const setCat = (i,k,v) => setCategorias(cs=>cs.map((c,idx)=>idx===i?{...c,[k]:v}:c));
  const addCat = () => setCategorias(cs=>[...cs,{categoria:"",codigo:"",descripcion:"",orden:(cs.length?Math.max(...cs.map(x=>+x.orden||0))+1:0),activo:"yes"}]);

  const handleSaveCategorias = async () => {
    setBusy(true);
    try {
      await api.put("/billing/categorias-codigo", { items: categorias });
      showMsg("Códigos de categoría guardados");
      load();
    } catch(e) { showMsg(e?.response?.data?.detail||"Error","error"); }
    finally { setBusy(false); }
  };

  const TABS = [
    { key:"dashboard", label:"Dashboard" },
    { key:"invoices",  label:"Facturas" },
    { key:"config",    label:"Configuración" },
  ];

  const now = new Date();
  const currentPeriod = now.getFullYear()+"-"+String(now.getMonth()+1).padStart(2,"0");

  return (
    <div>
      {dialogoConfirm}
      <div className="nv-page-header">
        <div>
          <div className="nv-page-title">Billing — Facturación</div>
          <div className="nv-page-sub">Periodo actual: {currentPeriod} · IVA {config?.tax_pct||12}%</div>
        </div>
        <div className="nv-page-actions">
          <button className="nv-btn nv-btn-ghost nv-btn-sm" onClick={handleProcessOverdue} disabled={busy}>
            ⚠ Procesar vencidas
          </button>
          <button className="nv-btn nv-btn-secondary nv-btn-sm" onClick={load} disabled={loading}>
            {loading?<span className="nv-spinner"/>:"↺"} Actualizar
          </button>
          <button className="nv-btn nv-btn-primary" onClick={handleGenerate} disabled={busy}>
            {busy?<span className="nv-spinner"/>:"+ Generar facturas"}
          </button>
        </div>
      </div>

      <div className="nv-kpi-grid" style={{ marginBottom:16 }}>
        <div className="nv-kpi">
          <div className="nv-kpi-label">Total facturado</div>
          <div className="nv-kpi-value" style={{ color:"var(--brand)" }}>{fmt$(stats?.total_billed)}</div>
          <div className="nv-kpi-sub">{currentPeriod}</div>
        </div>
        <div className="nv-kpi">
          <div className="nv-kpi-label">Cobrado</div>
          <div className="nv-kpi-value" style={{ color:"var(--success)" }}>{fmt$(stats?.collected)}</div>
          <div className="nv-kpi-sub">{stats?.paid||0} facturas pagadas</div>
        </div>
        <div className="nv-kpi">
          <div className="nv-kpi-label">Pendiente</div>
          <div className="nv-kpi-value" style={{ color:"var(--warning)" }}>{stats?.pending||0}</div>
          <div className="nv-kpi-sub">Por cobrar</div>
        </div>
        <div className="nv-kpi">
          <div className="nv-kpi-label">Vencidas</div>
          <div className="nv-kpi-value" style={{ color:(stats?.overdue||0)>0?"var(--danger)":"var(--success)" }}>{stats?.overdue||0}</div>
          <div className="nv-kpi-sub">Requieren acción</div>
        </div>
      </div>

      <Alert msg={msg} onClose={()=>setMsg(null)}/>

      <div className="nv-tabs">
        {TABS.map(t=>(<div key={t.key} className={"nv-tab"+(tab===t.key?" active":"")} onClick={()=>setTab(t.key)}>{t.label}</div>))}
      </div>

      {tab==="dashboard"&&(
        <div className="nv-grid-2">
          <div className="nv-card">
            <div className="nv-card-header"><span className="nv-card-title">◈ Resumen {currentPeriod}</span></div>
            {stats?(
              <div style={{ display:"flex",flexDirection:"column",gap:8 }}>
                {[
                  {lbl:"Total facturas",  val:stats.total_invoices||0,   color:"var(--text-primary)"},
                  {lbl:"Pagadas",         val:stats.paid||0,             color:"var(--success)"},
                  {lbl:"Pendientes",      val:stats.pending||0,          color:"var(--warning)"},
                  {lbl:"Vencidas",        val:stats.overdue||0,          color:"var(--danger)"},
                  {lbl:"Monto cobrado",   val:fmt$(stats.collected),     color:"var(--success)"},
                  {lbl:"Total facturado", val:fmt$(stats.total_billed),  color:"var(--brand)"},
                ].map(({lbl,val,color})=>(
                  <div key={lbl} style={{ display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:"1px solid var(--border-subtle)" }}>
                    <span style={{ fontSize:12,color:"var(--text-muted)" }}>{lbl}</span>
                    <span style={{ fontSize:13,fontWeight:700,color,fontFamily:"var(--font-mono)" }}>{val}</span>
                  </div>
                ))}
              </div>
            ):<div className="nv-loading"><span className="nv-spinner"/></div>}
          </div>
          <div className="nv-card">
            <div className="nv-card-header"><span className="nv-card-title">◈ Acciones rápidas</span></div>
            <div style={{ display:"flex",flexDirection:"column",gap:10 }}>
              <button className="nv-btn nv-btn-primary" onClick={handleGenerate} disabled={busy} style={{ justifyContent:"flex-start" }}>
                ▶ Generar facturas {currentPeriod}
              </button>
              <button className="nv-btn nv-btn-ghost" onClick={handleProcessOverdue} disabled={busy} style={{ justifyContent:"flex-start" }}>
                ⚠ Procesar vencidas y suspender
              </button>
              <div style={{ padding:"10px 12px",background:"var(--bg-raised)",borderRadius:"var(--r-sm)",fontSize:11,color:"var(--text-muted)",lineHeight:1.7 }}>
                <strong style={{ color:"var(--text-secondary)" }}>Flujo automático:</strong><br/>
                1. Generar facturas el día {config?.billing_day||1} de cada mes<br/>
                2. Notificar {config?.notify_days_before||3} días antes del vencimiento<br/>
                3. Suspender tras {config?.grace_days||5} días de gracia<br/>
                4. Reactivar automáticamente al pagar
              </div>
            </div>
          </div>
        </div>
      )}

      {tab==="invoices"&&(
        <div className="nv-card" style={{ padding:0 }}>
          {invoices.length===0?(
            <div style={{ textAlign:"center",padding:"48px 20px" }}>
              <div style={{ fontSize:32,opacity:.2,marginBottom:12 }}>▦</div>
              <div style={{ fontSize:13,color:"var(--text-muted)",marginBottom:16 }}>Sin facturas para {currentPeriod}</div>
              <button className="nv-btn nv-btn-primary" onClick={handleGenerate} disabled={busy}>
                + Generar facturas {currentPeriod}
              </button>
            </div>
          ):(
            <div className="nv-table-wrap">
              <table className="nv-table">
                <thead><tr>
                  <th>Cliente</th><th>Periodo</th><th>Monto</th><th>IVA</th><th>Total</th><th>Vence</th><th>Estado</th><th></th>
                </tr></thead>
                <tbody>
                  {invoices.map(inv=>(
                    <tr key={inv.id}>
                      <td style={{ fontWeight:500 }}>{inv.client_name||"—"}</td>
                      <td style={{ fontFamily:"var(--font-mono)",fontSize:11 }}>{inv.period}</td>
                      <td style={{ fontFamily:"var(--font-mono)" }}>{fmt$(inv.amount)}</td>
                      <td style={{ fontFamily:"var(--font-mono)",color:"var(--text-muted)" }}>{fmt$(inv.tax)}</td>
                      <td style={{ fontFamily:"var(--font-mono)",fontWeight:700,color:"var(--brand)" }}>{fmt$(inv.total)}</td>
                      <td style={{ fontSize:11,color:"var(--text-muted)" }}>{inv.due_date?.slice(0,10)}</td>
                      <td><span className={"nv-badge "+(STATUS_BADGE[inv.status]||"nv-badge-muted")}>{inv.status}</span></td>
                      <td style={{ whiteSpace:"nowrap" }}>
                        <button className="nv-btn nv-btn-ghost nv-btn-sm" onClick={()=>setFacturaId(inv.id)}>
                          📄 Ver factura
                        </button>
                        {inv.status!=="paid"&&(
                          <button className="nv-btn nv-btn-ghost nv-btn-sm" onClick={()=>handleMarkPaid(inv.id)}>
                            ✓ Marcar pagada
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {tab==="config"&&(
        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
        {config&&(
        <div className="nv-card" style={{ maxWidth:480 }}>
          <div className="nv-card-header"><span className="nv-card-title">⚙ Configuración billing</span></div>
          <div className="nv-form-row">
            <div className="nv-form-field">
              <label className="nv-label">Día de facturación</label>
              <input className="nv-input" type="number" min="1" max="28" value={config.billing_day}
                onChange={e=>setConfig(c=>({...c,billing_day:parseInt(e.target.value)}))}/>
            </div>
            <div className="nv-form-field">
              <label className="nv-label">Días de gracia</label>
              <input className="nv-input" type="number" min="0" max="30" value={config.grace_days}
                onChange={e=>setConfig(c=>({...c,grace_days:parseInt(e.target.value)}))}/>
            </div>
          </div>
          <div className="nv-form-row">
            <div className="nv-form-field">
              <label className="nv-label">IVA (%)</label>
              <input className="nv-input" type="number" step="0.01" value={config.tax_pct}
                onChange={e=>setConfig(c=>({...c,tax_pct:parseFloat(e.target.value)}))}/>
            </div>
            <div className="nv-form-field">
              <label className="nv-label">Notificar (días antes)</label>
              <input className="nv-input" type="number" min="0" max="15" value={config.notify_days_before}
                onChange={e=>setConfig(c=>({...c,notify_days_before:parseInt(e.target.value)}))}/>
            </div>
          </div>
          <div className="nv-form-row">
            <div className="nv-form-field">
              <label className="nv-label">Suspensión automática</label>
              <select className="nv-select" value={config.auto_suspend} onChange={e=>setConfig(c=>({...c,auto_suspend:e.target.value}))}>
                <option value="yes">Sí</option>
                <option value="no">No</option>
              </select>
            </div>
            <div className="nv-form-field">
              <label className="nv-label">Reactivación automática</label>
              <select className="nv-select" value={config.auto_reactivate} onChange={e=>setConfig(c=>({...c,auto_reactivate:e.target.value}))}>
                <option value="yes">Sí</option>
                <option value="no">No</option>
              </select>
            </div>
          </div>
          <div style={{ marginTop:8 }}>
            <button className="nv-btn nv-btn-primary" onClick={handleSaveConfig} disabled={busy}>
              {busy?<span className="nv-spinner"/>:"✓ Guardar configuración"}
            </button>
          </div>
        </div>
        )}

        {emisor&&(
        <div className="nv-card" style={{ maxWidth:720 }}>
          <div className="nv-card-header"><span className="nv-card-title">🏢 Datos del emisor (factura)</span></div>
          <div className="nv-form-row">
            <div className="nv-form-field">
              <label className="nv-label">Razón social</label>
              <input className="nv-input" value={emisor.razon_social||""} onChange={e=>setEm("razon_social",e.target.value)}/>
            </div>
            <div className="nv-form-field">
              <label className="nv-label">Nombre comercial</label>
              <input className="nv-input" value={emisor.nombre_comercial||""} onChange={e=>setEm("nombre_comercial",e.target.value)}/>
            </div>
          </div>
          <div className="nv-form-row">
            <div className="nv-form-field">
              <label className="nv-label">RUC</label>
              <input className="nv-input" value={emisor.ruc||""} onChange={e=>setEm("ruc",e.target.value)}/>
            </div>
            <div className="nv-form-field">
              <label className="nv-label">Contribuyente especial (Nº)</label>
              <input className="nv-input" value={emisor.contribuyente_especial||""} onChange={e=>setEm("contribuyente_especial",e.target.value)}/>
            </div>
          </div>
          <div className="nv-form-field">
            <label className="nv-label">Dirección matriz</label>
            <input className="nv-input" value={emisor.direccion_matriz||""} onChange={e=>setEm("direccion_matriz",e.target.value)}/>
          </div>
          <div className="nv-form-field">
            <label className="nv-label">Dirección sucursal</label>
            <input className="nv-input" value={emisor.direccion_sucursal||""} onChange={e=>setEm("direccion_sucursal",e.target.value)}/>
          </div>
          <div className="nv-form-row">
            <div className="nv-form-field">
              <label className="nv-label">Agente de retención (resolución)</label>
              <input className="nv-input" value={emisor.agente_retencion||""} onChange={e=>setEm("agente_retencion",e.target.value)}/>
            </div>
            <div className="nv-form-field">
              <label className="nv-label">Obligado a llevar contabilidad</label>
              <select className="nv-select" value={emisor.obligado_contabilidad||"SI"} onChange={e=>setEm("obligado_contabilidad",e.target.value)}>
                <option value="SI">SI</option>
                <option value="NO">NO</option>
              </select>
            </div>
          </div>
          <div className="nv-form-row">
            <div className="nv-form-field">
              <label className="nv-label">Establecimiento</label>
              <input className="nv-input" maxLength={3} value={emisor.establecimiento||""} onChange={e=>setEm("establecimiento",e.target.value)}/>
            </div>
            <div className="nv-form-field">
              <label className="nv-label">Punto de emisión</label>
              <input className="nv-input" maxLength={3} value={emisor.punto_emision||""} onChange={e=>setEm("punto_emision",e.target.value)}/>
            </div>
          </div>
          <div className="nv-form-field">
            <label className="nv-label">Texto legal (footer de la factura)</label>
            <textarea className="nv-input" rows={4} value={emisor.footer_legal||""} onChange={e=>setEm("footer_legal",e.target.value)}/>
          </div>
          <div style={{ marginTop:8 }}>
            <button className="nv-btn nv-btn-primary" onClick={handleSaveEmisor} disabled={busy}>
              {busy?<span className="nv-spinner"/>:"✓ Guardar emisor"}
            </button>
          </div>
        </div>
        )}

        <div className="nv-card">
          <div className="nv-card-header">
            <span className="nv-card-title">🏷 Códigos de línea por categoría</span>
            <button className="nv-btn nv-btn-ghost nv-btn-sm" onClick={addCat}>+ Agregar</button>
          </div>
          <div className="nv-table-wrap">
            <table className="nv-table">
              <thead><tr><th>Categoría</th><th>Código</th><th>Descripción</th><th>Orden</th><th>Activo</th></tr></thead>
              <tbody>
                {categorias.map((c,i)=>(
                  <tr key={i}>
                    <td><input className="nv-input" style={{ minWidth:90 }} readOnly={!c._new} value={c.categoria||""} onChange={e=>setCat(i,"categoria",e.target.value)}/></td>
                    <td><input className="nv-input" style={{ minWidth:100 }} value={c.codigo||""} onChange={e=>setCat(i,"codigo",e.target.value)}/></td>
                    <td><input className="nv-input" style={{ minWidth:160 }} value={c.descripcion||""} onChange={e=>setCat(i,"descripcion",e.target.value)}/></td>
                    <td><input className="nv-input" type="number" style={{ width:70 }} value={c.orden??0} onChange={e=>setCat(i,"orden",e.target.value)}/></td>
                    <td>
                      <select className="nv-select" value={c.activo||"yes"} onChange={e=>setCat(i,"activo",e.target.value)}>
                        <option value="yes">Sí</option>
                        <option value="no">No</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop:8 }}>
            <button className="nv-btn nv-btn-primary" onClick={handleSaveCategorias} disabled={busy}>
              {busy?<span className="nv-spinner"/>:"✓ Guardar códigos"}
            </button>
          </div>
          <div style={{ fontSize:11, color:"var(--text-muted)", marginTop:8, lineHeight:1.6 }}>
            La categoría <code>plan</code> es la renta mensual; el resto (local, regional, nacional, celular, internacional, onnet, 1800) mapea el consumo del CDR. El <strong>orden</strong> define la posición de la línea en la factura. La categoría de filas existentes no se edita (crearía un mapeo nuevo).
          </div>
        </div>
        </div>
      )}

      {facturaId&&<FacturaVista invoiceId={facturaId} onClose={()=>setFacturaId(null)}/>}
    </div>
  );
}
